import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, RefreshCw, Check, ShoppingCart, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '@/lib/calc';

export default function Purchasing() {
  const { role } = useAuth();
  const canApprove = ['admin','manager'].includes(role);
  const canCreate = ['admin','manager','purchasing'].includes(role);

  const [recs, setRecs] = useState([]);
  const [pos, setPos] = useState([]);
  const [inv, setInv] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [reqBefore, setReqBefore] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState('loading'); setError(null);
    try {
      const qs = reqBefore ? `?required_before=${encodeURIComponent(new Date(reqBefore).toISOString())}` : '';
      const [rec, po, iv] = await Promise.all([
        apiFetch(`/purchasing/recommendations${qs}`),
        apiFetch('/purchasing/pos'),
        apiFetch('/inventory/items'),
      ]);
      setRecs(rec || []); setPos(po || []); setInv(iv || []);
      setState('ok');
    } catch (e) { setError(e.message); setState('error'); }
  }, [reqBefore]);
  useEffect(() => { load(); }, [load]);

  const createPOFromRec = async (r) => {
    setBusy(true);
    try {
      const resp = await apiFetch('/purchasing/pos', {
        method: 'POST',
        body: JSON.stringify({
          supplier: 'TBD',
          required_date: r.earliest_required,
          lines: [{ sku: r.sku, qty: r.recommend_qty }],
        }),
      });
      toast.success(`Draft ${resp.number} created`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const approvePO = async (id, num) => {
    if (!window.confirm(`Approve ${num}? This is required before placing / exporting.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/purchasing/pos/${id}/approve`, { method: 'POST', body: '{}' });
      toast.success(`${num} approved`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="purchasing-page">
      <div className="velvet-eyebrow mb-3">Nexa · Purchasing</div>
      <h1 className="velvet-page-title mb-6">Purchasing workspace</h1>
      <div className="flex items-end gap-3 mb-8">
        <div>
          <Label>Required before</Label>
          <Input type="datetime-local" value={reqBefore} onChange={e => setReqBefore(e.target.value)} className="rounded-sm" data-testid="required-before-input" />
        </div>
        <Button variant="outline" className="rounded-sm" onClick={load} data-testid="refresh-recs-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {state === 'loading' && <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center" data-testid="purchasing-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {state === 'error' && (
        <div className="velvet-card p-8 text-center max-w-md mx-auto" data-testid="purchasing-error">
          <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button onClick={load} variant="outline" className="rounded-sm" data-testid="purchasing-retry"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry</Button>
        </div>
      )}

      {state === 'ok' && (
        <div className="grid lg:grid-cols-2 gap-8">
          <section className="velvet-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              <h2 className="velvet-section-title">Recommended purchases</h2>
              <span className="ml-auto text-xs text-muted-foreground" data-testid="rec-count">{recs.length}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-border">
                <th className="px-4 py-2 velvet-eyebrow">SKU</th>
                <th className="px-4 py-2 velvet-eyebrow">Item</th>
                <th className="px-4 py-2 velvet-eyebrow text-right">Need</th>
                <th className="px-4 py-2 velvet-eyebrow text-right">Have</th>
                <th className="px-4 py-2 velvet-eyebrow text-right">Incoming</th>
                <th className="px-4 py-2 velvet-eyebrow text-right">Buy</th>
                <th className="w-8"></th>
              </tr></thead>
              <tbody>
                {recs.map(r => (
                  <tr key={r.sku} className="border-b border-border last:border-b-0" data-testid={`rec-${r.sku}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
                    <td className="px-4 py-3">{r.name}<div className="text-[10px] text-muted-foreground">need by {fmtDate(r.earliest_required)}</div></td>
                    <td className="px-4 py-3 text-right velvet-money">{r.need}</td>
                    <td className="px-4 py-3 text-right velvet-money text-muted-foreground">{r.on_hand}</td>
                    <td className="px-4 py-3 text-right velvet-money text-muted-foreground">{r.incoming}</td>
                    <td className="px-4 py-3 text-right velvet-money font-medium" data-testid={`rec-buy-${r.sku}`}>{r.recommend_qty}</td>
                    <td className="px-2 py-3 text-right">
                      {canCreate && <button onClick={() => createPOFromRec(r)} disabled={busy} className="text-xs text-[hsl(var(--muted-green-dark))] hover:underline underline-offset-4" data-testid={`create-po-${r.sku}`}>Draft PO</button>}
                    </td>
                  </tr>
                ))}
                {recs.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground" data-testid="no-recs">No shortages — confirmed demand is covered.</td></tr>}
              </tbody>
            </table>
          </section>

          <section className="velvet-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />
              <h2 className="velvet-section-title">Purchase orders</h2>
              <span className="ml-auto text-xs text-muted-foreground">{pos.length}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-border">
                <th className="px-4 py-2 velvet-eyebrow">Number</th>
                <th className="px-4 py-2 velvet-eyebrow">Supplier</th>
                <th className="px-4 py-2 velvet-eyebrow">Required</th>
                <th className="px-4 py-2 velvet-eyebrow">Status</th>
                <th className="w-16"></th>
              </tr></thead>
              <tbody>
                {pos.map(p => (
                  <tr key={p.id} className="border-b border-border last:border-b-0" data-testid={`po-${p.number}`}>
                    <td className="px-4 py-3 font-mono text-xs">{p.number}</td>
                    <td className="px-4 py-3">{p.supplier || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.required_date)}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-border" data-testid={`po-status-${p.number}`}>{p.status}</span></td>
                    <td className="px-2 py-3 text-right">
                      {p.status === 'Draft' && canApprove && <button onClick={() => approvePO(p.id, p.number)} disabled={busy} className="text-xs text-[hsl(var(--muted-green-dark))] hover:underline underline-offset-4" data-testid={`approve-po-${p.number}`}><Check className="inline h-3 w-3 mr-0.5" />Approve</button>}
                    </td>
                  </tr>
                ))}
                {pos.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">No purchase orders yet.</td></tr>}
              </tbody>
            </table>
          </section>

          <section className="lg:col-span-2 velvet-card">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="velvet-section-title">Inventory (ATP = on-hand − reserved)</h2>
            </div>
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left border-b border-border sticky top-0 bg-card">
                  <th className="px-4 py-2 velvet-eyebrow">SKU</th>
                  <th className="px-4 py-2 velvet-eyebrow">Item</th>
                  <th className="px-4 py-2 velvet-eyebrow text-right">On hand</th>
                  <th className="px-4 py-2 velvet-eyebrow text-right">Reserved</th>
                  <th className="px-4 py-2 velvet-eyebrow text-right">ATP</th>
                  <th className="px-4 py-2 velvet-eyebrow text-right">Bunch</th>
                </tr></thead>
                <tbody>
                  {inv.filter(i => i.on_hand_qty > 0 || i.reserved_qty > 0).map(i => (
                    <tr key={i.sku} className="border-b border-border" data-testid={`inv-${i.sku}`}>
                      <td className="px-4 py-2 font-mono text-xs">{i.sku}</td>
                      <td className="px-4 py-2">{i.name}</td>
                      <td className="px-4 py-2 text-right velvet-money">{i.on_hand_qty}</td>
                      <td className="px-4 py-2 text-right velvet-money text-muted-foreground">{i.reserved_qty}</td>
                      <td className={`px-4 py-2 text-right velvet-money ${i.atp < 0 ? 'text-destructive font-medium' : ''}`} data-testid={`atp-${i.sku}`}>{i.atp}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{i.bunch_size}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
